/**
 * Real sidereal Vedic birth-chart calculation — replaces the deterministic
 * MOCK previously in src/lib/sidereal.ts (see that file's former header,
 * and docs/reference-calculator.md, which specified this module's
 * requirements against a commercial reference calculator).
 *
 * Pipeline:
 *   1. Resolve birthplace -> lat/lon + IANA timezone (cityLookup.ts).
 *   2. Resolve local birth wall-clock -> UTC instant using that timezone's
 *      historically-correct offset, incl. DST (timezone.ts).
 *   3. Real ephemeris (astronomy-engine, VSOP87-based, MIT-licensed, no
 *      native/WASM dependency) for: Sun's tropical ecliptic longitude (for
 *      Rashi/Moon-sign math we actually need the Moon, not the Sun — see
 *      below), Greenwich Apparent Sidereal Time, and the true obliquity of
 *      the ecliptic at that date (all needed for the Ascendant formula).
 *   4. Moon's true geocentric ecliptic longitude via
 *      `Astronomy.EclipticGeoMoon` (topocentric parallax is not applied —
 *      standard practice for natal Rashi/Nakshatra, which use the Moon's
 *      *sign*, not sub-degree apparent position from the birthplace).
 *   5. Ascendant (Lagna) via the standard spherical-astronomy formula
 *      (ascendant.ts).
 *   6. Mean lunar node (Rahu) via Meeus's formula (meanNode.ts) — Vedic
 *      astrology's conventional node choice (see that file's header).
 *   7. Subtract the Lahiri ayanamsha (ayanamsha.ts) from every tropical
 *      longitude above to get sidereal positions — this is the entire
 *      tropical/sidereal distinction the SEO guide and E-E-A-T audit
 *      (docs/seo-trust-audit.md) required us to actually deliver on.
 *   8. Derive Rashi (Moon's sidereal sign), Nakshatra + Pada (Moon's
 *      sidereal position within the 27 lunar mansions), Navamsa (D9,
 *      navamsa.ts) of the Moon, and the current Vimshottari Mahadasha
 *      (vimshottariDasha.ts).
 *
 * Precision note: this is VSOP87-truncated (astronomy-engine's own
 * documented ±1 arcminute accuracy) + a first-order linear Lahiri
 * ayanamsha approximation (ayanamsha.ts) — not a full Swiss-Ephemeris-grade
 * implementation. docs/reference-calculator.md's own re-verification against
 * a live commercial tool's output found this combination accurate to
 * within ~0.003° for the Ascendant and ~0.007° for a planet's sidereal
 * longitude on a real 1990 test case (see validate-ephemeris.mjs at the
 * repo root) — several orders of magnitude tighter than the ~3°20'
 * (Nakshatra) / 30° (Rashi) boundaries that actually matter for which
 * sign or lunar mansion a birth falls into, except for the rare birth that lands
 * within a couple of arcminutes of a sign/Nakshatra boundary. This is
 * documented, not silently glossed over, so a future swap to a full Swiss
 * Ephemeris binding (see ayanamsha.ts's header for the concrete npm
 * candidate) is a drop-in replacement of this module's internals only.
 */
import * as Astronomy from "astronomy-engine";
import { findCity, type CityMatch } from "./cityLookup";
import { zonedTimeToUtc } from "./timezone";
import { lahiriAyanamshaDegrees } from "./ayanamsha";
import { ascendantTropicalLongitude } from "./ascendant";
import { navamsaSignIndex } from "./navamsa";
import { computeCurrentMahadasha, VIMSHOTTARI_ORDER } from "./vimshottariDasha";

// meanNodeTropicalLongitude (./meanNode) is available for a future Rahu/Ketu
// field on ChartResult but not wired in yet — the current ChartData shape
// (lib/store.ts) has no Rahu/Ketu slot, and adding one is a UI/DB-schema
// decision outside this ephemeris swap's scope.

export type Rashi = {
  index: number; // 0..11
  name: string; // Sanskrit
  western: string; // familiar equivalent
  element: "Feuer" | "Erde" | "Luft" | "Wasser";
  lord: string; // ruling graha
};

export const RASHIS: Rashi[] = [
  { index: 0, name: "Mesha", western: "Widder", element: "Feuer", lord: "Mangala" },
  { index: 1, name: "Vrishabha", western: "Stier", element: "Erde", lord: "Shukra" },
  { index: 2, name: "Mithuna", western: "Zwillinge", element: "Luft", lord: "Budha" },
  { index: 3, name: "Karka", western: "Krebs", element: "Wasser", lord: "Chandra" },
  { index: 4, name: "Simha", western: "Löwe", element: "Feuer", lord: "Surya" },
  { index: 5, name: "Kanya", western: "Jungfrau", element: "Erde", lord: "Budha" },
  { index: 6, name: "Tula", western: "Waage", element: "Luft", lord: "Shukra" },
  { index: 7, name: "Vrishchika", western: "Skorpion", element: "Wasser", lord: "Mangala" },
  { index: 8, name: "Dhanu", western: "Schütze", element: "Feuer", lord: "Guru" },
  { index: 9, name: "Makara", western: "Steinbock", element: "Erde", lord: "Shani" },
  { index: 10, name: "Kumbha", western: "Wassermann", element: "Luft", lord: "Shani" },
  { index: 11, name: "Meena", western: "Fische", element: "Wasser", lord: "Guru" },
];

export const NAKSHATRAS: string[] = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashira", "Ardra", "Punarvasu",
  "Pushya", "Ashlesha", "Magha", "Purva Phalguni", "Uttara Phalguni", "Hasta",
  "Chitra", "Swati", "Vishakha", "Anuradha", "Jyeshtha", "Mula", "Purva Ashadha",
  "Uttara Ashadha", "Shravana", "Dhanishta", "Shatabhisha", "Purva Bhadrapada",
  "Uttara Bhadrapada", "Revati",
];

export type BirthInput = {
  dob: string; // "DD.MM.YYYY"
  time: string; // "HH:MM" or "HH:MM:SS"
  city: string;
  /** ISO-3166-1 alpha-2 country code (e.g. "DE"), from the form's new
   *  country select (see BirthChartForm.tsx). Optional for backward
   *  compatibility with any existing callers, but strongly recommended:
   *  without it, an ambiguous city name (see cityLookup.ts's findCity()
   *  header for why this matters) can silently resolve to the wrong
   *  country/timezone. */
  countryCode?: string;
};

export type ChartResult = {
  lagna: Rashi;
  rashi: Rashi;
  nakshatra: string;
  pada: number; // 1-4
  navamsaRashi: Rashi;
  premium: {
    currentDasha: string;
    dashaLordSanskrit: string;
    dashaStart: string; // ISO date
    dashaEnd: string; // ISO date
  };
  /** Resolved birthplace, exposed for storage/debugging (see calculator/service.ts). */
  resolvedCity: CityMatch;
  /** UTC instant used for the calculation, exposed for storage/debugging. */
  utcInstant: string; // ISO
};

export class CityNotFoundError extends Error {
  constructor(city: string) {
    super(`Geburtsort "${city}" konnte nicht gefunden werden.`);
    this.name = "CityNotFoundError";
  }
}

export class InvalidBirthInputError extends Error {}

function parseDob(dob: string): { day: number; month: number; year: number } {
  const parts = dob.split(".");
  if (parts.length !== 3) throw new InvalidBirthInputError(`Ungültiges Geburtsdatum: "${dob}" (erwartet TT.MM.JJJJ).`);
  const [dd, mm, yyyy] = parts;
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year = parseInt(yyyy, 10);
  if (!day || !month || !year || month < 1 || month > 12 || day < 1 || day > 31) {
    throw new InvalidBirthInputError(`Ungültiges Geburtsdatum: "${dob}" (erwartet TT.MM.JJJJ).`);
  }
  return { day, month, year };
}

function parseTime(time: string): { hour: number; minute: number; second: number } {
  // Accepts "HH:MM" (legacy) and "HH:MM:SS" (3-box TimeInput). Seconds are
  // optional and default to 0; when present they flow into the UTC instant
  // below so the lagna/ascendant reflects second-level birth precision
  // (the ascendant advances ~1° every ~4 minutes, so seconds matter for
  // births near a sign cusp).
  const parts = time.split(":");
  if (parts.length !== 2 && parts.length !== 3) {
    throw new InvalidBirthInputError(`Ungültige Geburtszeit: "${time}" (erwartet HH:MM oder HH:MM:SS).`);
  }
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  const second = parts.length === 3 ? parseInt(parts[2], 10) : 0;
  if (
    Number.isNaN(hour) || Number.isNaN(minute) || Number.isNaN(second) ||
    hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59
  ) {
    throw new InvalidBirthInputError(`Ungültige Geburtszeit: "${time}" (erwartet HH:MM oder HH:MM:SS).`);
  }
  return { hour, minute, second };
}

function normalize360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function siderealSignIndex(siderealLongitudeDeg: number): number {
  return Math.floor(normalize360(siderealLongitudeDeg) / 30) % 12;
}

/** Runs the full real-ephemeris chart calculation. Async because city resolution loads a static JSON file (see cityLookup.ts). */
export async function computeChart(input: BirthInput): Promise<ChartResult> {
  const { day, month, year } = parseDob(input.dob);
  const { hour, minute, second } = parseTime(input.time);

  const city = await findCity(input.city, input.countryCode);
  if (!city) throw new CityNotFoundError(input.city);

  const utcInstant = zonedTimeToUtc(year, month, day, hour, minute, second, city.timezone);
  const time = Astronomy.MakeTime(utcInstant);

  const ayanamsha = lahiriAyanamshaDegrees(time);

  // Moon's true geocentric ecliptic longitude (tropical, of-date).
  const moonEcliptic = Astronomy.EclipticGeoMoon(time);
  const moonTropical = moonEcliptic.lon;
  const moonSidereal = normalize360(moonTropical - ayanamsha);

  // Ascendant (Lagna).
  const gastHours = Astronomy.SiderealTime(time);
  const tilt = Astronomy.e_tilt(time);
  const localSiderealTimeDeg = gastHours * 15 + city.longitude;
  const ascendantTropicalDeg = ascendantTropicalLongitude(localSiderealTimeDeg, city.latitude, tilt.tobl);
  const ascendantSidereal = normalize360(ascendantTropicalDeg - ayanamsha);

  const rashi = RASHIS[siderealSignIndex(moonSidereal)];
  const lagna = RASHIS[siderealSignIndex(ascendantSidereal)];

  const NAKSHATRA_SPAN_DEG = 360 / 27;
  const nakshatraIndex = Math.floor(normalize360(moonSidereal) / NAKSHATRA_SPAN_DEG) % 27;
  const nakshatra = NAKSHATRAS[nakshatraIndex];
  const PADA_SPAN_DEG = NAKSHATRA_SPAN_DEG / 4;
  const pada = (Math.floor(normalize360(moonSidereal) / PADA_SPAN_DEG) % 4) + 1;

  const navamsaRashi = RASHIS[navamsaSignIndex(moonSidereal)];

  const birthDate = utcInstant;
  // Bug fix: this previously passed `birthDate` as both the `birthDate` and
  // `now` argument, which made computeCurrentMahadasha() always return the
  // Mahadasha that was running AT BIRTH, not the one running TODAY — e.g. a
  // 1990 birth would incorrectly show its birth-era Mahadasha instead of
  // walking forward ~36 years through the Vimshottari cycle to whichever
  // Mahadasha is actually active now. `now` must be the real current time.
  const dasha = computeCurrentMahadasha(moonSidereal, birthDate, new Date());
  const dashaLordEntry = VIMSHOTTARI_ORDER.find((l) => l.sanskrit === dasha.lord.sanskrit)!;

  return {
    lagna,
    rashi,
    nakshatra,
    pada,
    navamsaRashi,
    premium: {
      currentDasha: `${dashaLordEntry.german}-Mahadasha`,
      dashaLordSanskrit: dashaLordEntry.sanskrit,
      dashaStart: dasha.start.toISOString(),
      dashaEnd: dasha.end.toISOString(),
    },
    resolvedCity: city,
    utcInstant: utcInstant.toISOString(),
  };
}
